import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Package } from '../package.models';
import { ConfirmationDialogService } from '../../common/services/confirmation-dialog.service';
import { AppState } from '../../store';
import { ItemType } from '../../common/components/lists/list-item-snippet.component';
import { duplicatePackage, removePackage } from '../store/packages.actions';
import { selectWorkspacesFeatureFlag } from '../../account/store/account.selectors';
import { PermissionsService } from '../../common/services/permissions.service';

@Component({
  selector: 'package-list-item',
  template: `
    <div class="generic-list-item slider-animation" [ngClass]="{ 'closed-animation': item.isRemoved }">
      <div
        class="package-list-item"
        [ngClass]="{
          selected: item.selected,
          'pending-removal': item.pending_removal,
          inactive: item.status === 'archived'
        }"
        id="package-{{ item.id }}"
      >
        <div class="package-name">
          <svg
            *ngIf="item.flow_type === 'workflow'"
            class="icon icons-master-expanded_icon-navmain-packages icon-workflow"
          >
            <use href="#img-sprite_icon-icons-master-expanded_icon-navmain-workflow"></use>
          </svg>
          <svg *ngIf="item.flow_type === 'dataflow'" class="icon icons-master-expanded_icon-navmain-packages">
            <use href="#img-sprite_icons-master-expanded_icon-navmain-packages"></use>
          </svg>
          <div>
            <strong
              ><a
                *ngIf="(isUpdatePackageEnabled$ | async) && !(isNewPackageDesignerEnabled$ | async)"
                (click)="goToPackage(item, $event)"
                >{{ item.name | xpLengthCheck: ((isWorkspacesFeatureEnabled$ | async) ? 34 : 40) }}</a
              >
              <a
                *ngIf="(isUpdatePackageEnabled$ | async) && (isNewPackageDesignerEnabled$ | async)"
                (click)="goToPackageNew(item, $event)"
                >{{ item.name | xpLengthCheck: ((isWorkspacesFeatureEnabled$ | async) ? 34 : 40) }}</a
              >
              <span *ngIf="item.status == 'archived'" class="inactive"
                >({{ 'package.generic-list.item.labels.inactive' | translate }})</span
              ></strong
            >
            <p *ngIf="item.description">
              {{ item.description | xpLengthCheck: ((isWorkspacesFeatureEnabled$ | async) ? 45 : 80) }}
            </p>
            <span *ngxPermissionsOnly="'versionControl'">
              <a class="package-version" (click)="editPackageVersion(item)"> Version {{ item.package_version }} </a>
              <span *ngIf="item.version_description" class="version-message">
                - {{ item.version_description | xpLengthCheck: ((isWorkspacesFeatureEnabled$ | async) ? 50 : 150) }}
              </span>
            </span>
          </div>
        </div>
        <div class="package-workspace" *ngIf="(isWorkspacesFeatureEnabled$ | async) && !isWorkspacesItemView">
          <a *ngIf="item.workspace">{{ item.workspace.name }}</a>
          <span *ngIf="!item.workspace"></span>
        </div>
        <div class="package-owner">
          <a (click)="goToOwner(item, $event)" *ngIf="item.owner">{{
            item.owner.name || item.owner.email | xpLengthCheck: 20
          }}</a>
        </div>
        <div class="package-date">
          <span date-tooltip="item.updated_at">{{ item.updated_at | xpPrettyDate }}</span>
        </div>
        <div class="package-run-job">
          <svg class="icon icons-master-expanded_icon-general-play" (click)="runJob.emit(item)">
            <use href="#img-sprite_icons-master-expanded_icon-general-play"></use>
          </svg>
          <strong>{{ 'package.generic-list.item.labels.run_job' | translate }}</strong>
        </div>
        <div class="dropdown xp-dropdown-full" [matMenuTriggerFor]="dropdown">
          <i class="fa fa-ellipsis-v"></i>
        </div>
        <mat-menu #dropdown="matMenu" class="full-dropdown-menu">
          <li
            *ngIf="(isUpdatePackageEnabled$ | async) && (isNewPackageDesignerEnabled$ | async)"
            mat-menu-item
            (click)="goToPackageNew(item, $event)"
          >
            <span>{{ 'package.generic-list.item.actions.edit' | translate }}</span>
          </li>
          <li
            *ngIf="(isUpdatePackageEnabled$ | async) && !(isNewPackageDesignerEnabled$ | async)"
            mat-menu-item
            (click)="goToPackage(item)"
          >
            <span>{{ 'package.generic-list.item.actions.edit' | translate }}</span>
          </li>
          <li
            *ngIf="(isUpdatePackageEnabled$ | async) && !(isNewPackageDesignerEnabled$ | async)"
            mat-menu-item
            (click)="goToPackageNew(item, $event)"
          >
            <span>{{ 'package.generic-list.item.actions.edit-new' | translate }}</span>
          </li>
          <li mat-menu-item (click)="editPackage(item)" *ngxPermissionsOnly="'updatePackage'">
            <span>{{ 'package.generic-list.item.actions.rename' | translate }}</span>
          </li>
          <li mat-menu-item (click)="duplicatePackage(item)" *ngxPermissionsOnly="'createPackage'">
            <span>{{ 'package.generic-list.item.actions.duplicate' | translate }}</span>
          </li>
          <li mat-menu-item (click)="editPackageWorkspace(item)" *ngIf="isWorkspacesFeatureEnabled$ | async">
            <span *ngIf="!item.workspace_id">{{
              'package.generic-list.item.actions.add-to-workspace' | translate
            }}</span>
            <span *ngIf="item.workspace_id">{{
              'package.generic-list.item.actions.change-workspace' | translate
            }}</span>
          </li>
          <li
            mat-menu-item
            [hidden]="item.status === 'archived'"
            (click)="archivePackage(item)"
            *ngxPermissionsOnly="'deletePackage'"
            class="top-separator"
          >
            <a>
              <span class="text-warning">{{ 'package.generic-list.item.actions.archive' | translate }}</span>
            </a>
          </li>
        </mat-menu>
      </div>
    </div>
  `,
})
export class PackageListItemComponent implements OnInit {
  @Input() item: Partial<Package> = {};
  @Output() edit = new EventEmitter<Partial<Package>>();
  @Output() editVersion = new EventEmitter<Partial<Package>>();
  @Output() editWorkspace = new EventEmitter<Partial<Package>>();
  @Output() runJob = new EventEmitter<Partial<Package>>();
  isWorkspacesItemView = false;
  isWorkspacesFeatureEnabled$ = this.store.select(selectWorkspacesFeatureFlag);
  isNewPackageDesignerEnabled$ = this.permissionsService.hasPermission('newPackageDesigner');
  isUpdatePackageEnabled$ = this.permissionsService.hasPermission('updatePackage');

  constructor(
    private translate: TranslateService,
    private confirmationDialog: ConfirmationDialogService,
    private store: Store<AppState>,
    private permissionsService: PermissionsService,
    @Inject('$rootScope') private $rootScope: any,
  ) {}

  ngOnInit() {
    if (!this.item) {
      this.item = {};
    }

    this.isWorkspacesItemView = this.$rootScope.stateIs('app.account.workspaces-item');
  }

  editPackage(item: Partial<Package>) {
    this.edit.emit(item);
  }

  editPackageWorkspace(item: Partial<Package>) {
    this.editWorkspace.emit(item);
  }

  editPackageVersion(item: Partial<Package>) {
    this.editVersion.emit(item);
  }

  goToPackage(item: Partial<Package>, event: any = {}): void {
    const openInNewTab = event.metaKey || event.ctrlKey || event.isMiddleClick;
    this.$rootScope.stateGo('app.account.packages.item.edit', { id: item.id }, openInNewTab);
  }

  goToPackageNew(item: Partial<Package>, event: any): void {
    const openInNewTab = event.metaKey || event.ctrlKey || event.isMiddleClick;
    this.$rootScope.stateGo('app.account.packages.item.edit-new', { id: item.id }, openInNewTab);
  }

  goToOwner(item: Partial<Package>, event: any): void {
    const openInNewTab = event.metaKey || event.ctrlKey || event.isMiddleClick;
    this.$rootScope.stateGo('app.account.settings.account.member', item.owner, openInNewTab);
  }

  archivePackage(item: Partial<Package>): void {
    const dialogRef = this.confirmationDialog.openDialog({
      title: this.translate.instant('package.generic-list.item.modals.confirm.title'),
      hint: this.translate.instant('package.generic-list.item.modals.confirm.hint'),
      yes: this.translate.instant('package.generic-list.item.modals.confirm.yes'),
      no: this.translate.instant('package.generic-list.item.modals.confirm.no'),
      item,
      itemType: ItemType.package,
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.store.dispatch(removePackage({ packageId: item.id }));
      }
    });
  }

  duplicatePackage(item: Partial<Package>): void {
    this.store.dispatch(duplicatePackage({ packageId: item.id, packageName: item.name }));
  }
}
